import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { checkoutSubsciptions, fetchSubscriptionProducts, fetchInvoices, cancelSubscription } from '../../redux/PaymentSlice/paymentSlice';
import { fetchUserProfile } from '../../redux/AuthSlice/authSlice';
import { performanceMonitor } from '../../utils/performanceMonitor';
import { Sparkles, Check, HelpCircle, ArrowRight, FileText, CreditCard, Mail, AlertTriangle } from 'lucide-react';
import Navbaar from '../../components/Navbar/Navbaar';
import Sidebar from '../../components/Navbar/SideNavbar';

// ─── static plan data ─────────────────────────────────────────────────────────

const PLAN_META = {
    free:      { tag: null,           recommended: false, features: ['3–5 hiring signals / week', 'Basic job matching', 'Limited Plays'] },
    weekly:    { tag: 'Burst hiring', recommended: false, features: ['Full hiring signals', 'Smarter matching', 'Unlimited Plays'] },
    monthly:   { tag: 'Recommended', recommended: true,  features: ['Everything in Weekly', 'Priority AI quota', 'Unlimited recruiter lookups', 'Daily coaching'] },
    quarterly: { tag: 'Save 22%',    recommended: false, features: ['Everything in Monthly', '3 months locked in', 'Quarterly career report', 'Founding-member discount lock'] },
};

const COMPARE_ROWS = [
    { label: 'Hiring signal access',    free: 'Limited',   weekly: 'Full',      monthly: 'Full',      quarterly: 'Full' },
    { label: 'AI Assistant',            free: '5 / day',   weekly: 'Unlimited', monthly: 'Unlimited', quarterly: 'Unlimited' },
    { label: 'Recruiter lookups',       free: '3 / week',  weekly: '50',        monthly: '200',       quarterly: '600' },
    { label: 'Daily tasks coach',       free: '—',         weekly: '—',         monthly: '✓',         quarterly: '✓' },
    { label: 'Quarterly career report', free: '—',         weekly: '—',         monthly: '—',         quarterly: '✓' },
    { label: 'Priority support',        free: '—',         weekly: '✓',         monthly: '✓',         quarterly: '✓' },
];

const FAQ = [
    { q: 'Can I switch plans anytime?',     a: 'Yes — upgrade or downgrade in one click. We prorate the difference and credit your account.' },
    { q: 'What happens to unused credits?', a: 'Credits roll over forever on Monthly and Quarterly. Weekly resets each cycle.' },
    { q: 'Is there a refund window?',       a: '7-day money-back guarantee on every paid plan, no questions asked.' },
];

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatUsd  = (cents) => cents == null ? '0' : (Number(cents) / 100).toFixed(0);
const cycleFor   = (p) => p?.plan?.billingCycle || p?.metadata?.billing;
const orderIndex = (p) => ({ weekly: 0, monthly: 1, quarterly: 2, annual: 3 })[cycleFor(p)] ?? 99;

const intervalLabel = (p) => {
    const c = cycleFor(p);
    if (c === 'weekly')    return 'per week';
    if (c === 'quarterly') return 'per quarter';
    if (c === 'annual')    return 'per year';
    return 'per month';
};

const planTitle = (p) => {
    const c = cycleFor(p);
    if (c === 'weekly')    return 'Weekly';
    if (c === 'quarterly') return 'Quarterly';
    if (c === 'annual')    return 'Annual';
    return 'Monthly';
};

function getRenewalDate(currentPeriodEnd) {
    if (!currentPeriodEnd) return null;
    let d;
    if (currentPeriodEnd?.$date?.$numberLong) {
        d = new Date(parseInt(currentPeriodEnd.$date.$numberLong, 10));
    } else if (typeof currentPeriodEnd === 'string' || typeof currentPeriodEnd === 'number') {
        d = new Date(currentPeriodEnd);
    } else {
        return null;
    }
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

// ─── skeleton block ───────────────────────────────────────────────────────────
const Sk = ({ className = '' }) => (
    <div className={`animate-pulse bg-[#E9EBF0] rounded-lg ${className}`} />
);

// ─── component ────────────────────────────────────────────────────────────────

const SubscriptionPage = () => {
    const dispatch  = useDispatch();
    const navigate  = useNavigate();
    const user      = useSelector((s) => s.auth.user);
    const { checkoutLoading, subscriptionProducts, subLoading, subError, invoices, invoicesLoading, cancelLoading } = useSelector((s) => s.payments);

    const [clickedPriceId, setClickedPriceId] = useState(null);
    const [checkoutError,  setCheckoutError]  = useState(null);
    const [cancelConfirm,  setCancelConfirm]  = useState(false);
    const [cancelDone,     setCancelDone]     = useState(false);
    const [selectedPlan,   setSelectedPlan]   = useState('monthly');

    const sortedProducts = useMemo(() => {
        const list = Array.isArray(subscriptionProducts) ? [...subscriptionProducts] : [];
        return list.sort((a, b) => {
            const d = orderIndex(a) - orderIndex(b);
            return d !== 0 ? d : (a.unit_amount || 0) - (b.unit_amount || 0);
        });
    }, [subscriptionProducts]);

    const isFreeUser       = !user?.subscription?.subscriptionId || user?.subscription?.tier === 'free';
    const currentTier      = user?.subscription?.tier || 'free';
    const currentCycleName = user?.billingCycle || user?.subscription?.billingCycle;
    const renewalDate      = getRenewalDate(user?.subscription?.currentPeriodEnd);
    const creditsBalance   = user?.creditsBalance ?? null;

    const isCurrentPaidCard = (p) => {
        if (!user?.subscription?.subscriptionId || currentTier === 'free') return false;
        return currentCycleName === cycleFor(p);
    };

    useEffect(() => {
        dispatch(fetchSubscriptionProducts());
        dispatch(fetchInvoices());
    }, [dispatch]);

    const handleCheckout = async (product) => {
        const priceId      = product.id;
        const billingCycle = cycleFor(product) || 'monthly';
        setClickedPriceId(priceId);
        setCheckoutError(null);
        try {
            performanceMonitor.start('checkoutSubscription', 'api');
            const response = await dispatch(checkoutSubsciptions({ priceId, billingCycle })).unwrap();
            performanceMonitor.end('checkoutSubscription', 'api');
            if (response?.url) { window.location.href = response.url; return; }
            setCheckoutError('Could not start checkout. Please try again.');
        } catch {
            performanceMonitor.end('checkoutSubscription', 'api', false);
            setCheckoutError('The subscription service is currently unavailable. Please try again later.');
            setTimeout(() => setClickedPriceId(null), 3000);
        }
    };

    const handleCancel = async () => {
        if (!cancelConfirm) { setCancelConfirm(true); return; }
        try {
            await dispatch(cancelSubscription()).unwrap();
            await dispatch(fetchUserProfile());
            setCancelDone(true);
            setCancelConfirm(false);
        } catch {
            setCancelConfirm(false);
        }
    };

    // Find the quarterly product for "Switch to Quarterly" CTA
    const quarterlyProduct = sortedProducts.find((p) => cycleFor(p) === 'quarterly');

    // Format invoice date
    const fmtInvoiceDate = (ts) => new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const fmtAmount = (cents, currency = 'usd') => {
        const sym = currency === 'eur' ? '€' : currency === 'inr' ? '₹' : '$';
        return `${sym}${(cents / 100).toFixed(2)}`;
    };

    // Build all 4 plan columns
    const allPlans = [
        {
            key:      'free',
            title:    'Free',
            price:    '$0',
            per:      '7-day trial',
            crossed:  null,
            isCurrent: isFreeUser,
            isLoading: false,
            meta:     PLAN_META.free,
            onSelect: () => isFreeUser && navigate('/'),
            ctaLabel: isFreeUser ? 'Current plan' : 'Continue free',
            disabled: isFreeUser,
        },
        ...sortedProducts.map((p) => {
            const cycle    = cycleFor(p);
            const isCurrent = isCurrentPaidCard(p);
            return {
                key:       cycle || p.id,
                title:     planTitle(p),
                price:     `$${formatUsd(p.unit_amount)}`,
                per:       intervalLabel(p),
                crossed:   cycle === 'quarterly' ? `$${Math.round(Number(formatUsd(p.unit_amount)) / 3 * 12 / 3 * 4)}` : null,
                isCurrent,
                isLoading: checkoutLoading && clickedPriceId === p.id,
                meta:      PLAN_META[cycle] || PLAN_META.monthly,
                onSelect:  () => !isCurrent && handleCheckout(p),
                ctaLabel:  isCurrent ? 'Current plan' : (PLAN_META[cycle]?.recommended ? 'Continue with Monthly' : cycle === 'quarterly' ? 'Lock in 3 months' : cycle === 'weekly' ? 'Try this week' : 'Get plan'),
                disabled:  checkoutLoading || isCurrent,
                error:     checkoutError && clickedPriceId === p.id ? checkoutError : null,
            };
        }),
    ];

    const isLoadingProducts = subLoading && sortedProducts.length === 0;

    return (
        <div className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col">
            <div className="flex w-full">
                <Navbaar />
            </div>
            <div className="flex flex-row flex-1 min-h-0 transition-all duration-300">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                <div className="flex flex-1 overflow-y-auto w-full">
                    <div className="w-full px-4 py-5 flex flex-col gap-5 text-left">

                        {/* ── skeleton ── */}
                        {isLoadingProducts && (
                            <div className="flex flex-col gap-5">
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                                    {[1,2,3,4].map(i => (
                                        <div key={i} className="rounded-2xl border border-[#E6E8EE] bg-white p-5 flex flex-col gap-4">
                                            <div className="flex items-center justify-between">
                                                <Sk className="h-4 w-20" />
                                                <Sk className="h-5 w-16 rounded-full" />
                                            </div>
                                            <Sk className="h-9 w-24" />
                                            <Sk className="h-3 w-20" />
                                            <div className="flex flex-col gap-2 mt-1">
                                                {[1,2,3].map(j => <Sk key={j} className="h-3 w-full" />)}
                                            </div>
                                            <Sk className="h-10 w-full mt-auto" />
                                        </div>
                                    ))}
                                </div>
                                <div className="rounded-2xl border border-[#E6E8EE] bg-white p-5">
                                    <Sk className="h-4 w-40 mb-4" />
                                    <div className="flex flex-col gap-3">
                                        {[1,2,3,4,5,6].map(i => (
                                            <div key={i} className="flex gap-4 items-center py-2 border-b border-[#EFF1F5]">
                                                <Sk className="h-3 flex-1" />
                                                {[1,2,3,4].map(j => <Sk key={j} className="h-3 w-16" />)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isLoadingProducts && <>

                        {/* ── current plan status strip ── */}
                        {!isFreeUser && (
                            <div
                                className="flex items-center gap-3 px-5 py-3.5 rounded-2xl border"
                                style={{ background: 'linear-gradient(135deg, #EEF1FE 0%, #fff 70%)', borderColor: '#C7C9F1' }}
                            >
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{ background: '#fff', border: '1px solid #C7C9F1' }}>
                                    <Sparkles size={15} style={{ color: '#3F57E0' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13.5px] font-bold text-[#0F172A] font-dmsans">
                                        You're on{' '}
                                        <span style={{ color: '#3F57E0' }}>
                                            {currentCycleName
                                                ? currentCycleName.charAt(0).toUpperCase() + currentCycleName.slice(1)
                                                : currentTier.charAt(0).toUpperCase() + currentTier.slice(1)
                                            } · Pro
                                        </span>
                                    </div>
                                    <div className="text-[11.5px] text-[#64748B] mt-0.5 font-dmsans flex items-center gap-1.5 flex-wrap">
                                        {renewalDate && <span>Renews {renewalDate}</span>}
                                        {renewalDate && creditsBalance != null && <span>·</span>}
                                        {creditsBalance != null && <span>{creditsBalance} credits remaining</span>}
                                        {currentCycleName === 'monthly' && (
                                            <><span>·</span><span className="text-[#3F57E0] font-semibold">save 22% by switching to Quarterly</span></>
                                        )}
                                    </div>
                                </div>
                                {!user?.subscription?.cancelAtPeriodEnd && !cancelDone && (
                                    <button
                                        type="button"
                                        onClick={() => { document.getElementById('cancel-section')?.scrollIntoView({ behavior: 'smooth' }); }}
                                        className="text-[12px] font-semibold font-dmsans text-red-500 hover:text-red-600 transition-colors px-2 py-1 flex-shrink-0"
                                    >
                                        Cancel plan
                                    </button>
                                )}
                                {currentCycleName === 'monthly' && quarterlyProduct && (
                                    <button
                                        type="button"
                                        onClick={() => handleCheckout(quarterlyProduct)}
                                        disabled={checkoutLoading}
                                        className="flex items-center gap-1.5 text-[12px] font-semibold font-dmsans text-white px-3.5 py-2 rounded-xl hover:opacity-90 transition-opacity flex-shrink-0 disabled:opacity-60"
                                        style={{ backgroundColor: '#3F57E0' }}
                                    >
                                        Switch to Quarterly
                                        <ArrowRight size={13} />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* section title */}
                        <div>
                            <h2 className="text-[15px] font-bold text-[#0F172A] font-dmsans">Choose your plan</h2>
                            <p className="text-[12.5px] text-[#94A3B8] mt-0.5 font-dmsans">Monthly is our standard option. Switch billing anytime before checkout.</p>
                        </div>

                        {/* ── loading skeleton ── */}
                        {subLoading && (
                            <div className="grid grid-cols-4 gap-3">
                                {[1,2,3,4].map(k => (
                                    <div key={k} className="rounded-xl border border-[#E6E8EE] p-5 flex flex-col gap-3">
                                        <div className="h-4 w-16 bg-gray-100 animate-pulse rounded" />
                                        <div className="h-8 w-20 bg-gray-100 animate-pulse rounded" />
                                        <div className="h-3 w-full bg-gray-100 animate-pulse rounded" />
                                        <div className="h-3 w-4/5 bg-gray-100 animate-pulse rounded" />
                                        <div className="h-9 w-full bg-gray-100 animate-pulse rounded-lg mt-2" />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ── error ── */}
                        {subError && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
                                <p className="text-red-600 font-medium font-dmsans mb-3">Error loading plans</p>
                                <p className="text-[13px] text-red-500 mb-4">{subError}</p>
                                <button
                                    type="button"
                                    onClick={() => dispatch(fetchSubscriptionProducts())}
                                    className="text-[13px] font-semibold text-white px-4 py-2 rounded-lg"
                                    style={{ backgroundColor: '#3F57E0' }}
                                >
                                    Try Again
                                </button>
                            </div>
                        )}

                        {/* ── plan cards ── */}
                        {!subLoading && !subError && (
                            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${allPlans.length}, 1fr)` }}>
                                {allPlans.map((plan) => {
                                    const rec = plan.meta?.recommended;
                                    const sel = selectedPlan === plan.key;
                                    return (
                                        <div
                                            key={plan.key}
                                            onClick={() => setSelectedPlan(plan.key)}
                                            className="relative flex flex-col gap-3 rounded-2xl p-5 transition-all cursor-pointer"
                                            style={{
                                                border: sel ? '2px solid #3F57E0' : '1px solid #E6E8EE',
                                                background: '#fff',
                                                boxShadow: sel ? '0 18px 32px rgba(63,87,224,.12)' : '0 1px 3px rgba(15,23,42,.04)',
                                                transform: rec ? 'translateY(-4px)' : 'none',
                                            }}
                                        >
                                            {/* tag pill */}
                                            {plan.meta?.tag && !plan.isCurrent && (
                                                <span
                                                    className="absolute -top-3 left-4 text-[10px] font-bold uppercase tracking-[.08em] text-white px-2.5 py-1 rounded-full"
                                                    style={{ background: rec ? '#3F57E0' : plan.key === 'quarterly' ? '#10B981' : '#0F172A' }}
                                                >
                                                    {plan.meta.tag}
                                                </span>
                                            )}
                                            {plan.isCurrent && (
                                                <span className="absolute -top-3 left-4 text-[10px] font-bold uppercase tracking-[.08em] text-[#3F57E0] bg-[#EEF1FE] px-2.5 py-1 rounded-full">
                                                    Current
                                                </span>
                                            )}

                                            {/* price block */}
                                            <div>
                                                <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans">{plan.title}</div>
                                                <div className="flex items-baseline gap-2 mt-1.5 flex-wrap">
                                                    <span className="text-[30px] font-extrabold tracking-tight text-[#0F172A] font-dmsans leading-none">{plan.price}</span>
                                                    {plan.crossed && (
                                                        <span className="text-[12px] text-[#94A3B8] line-through font-dmsans">{plan.crossed}</span>
                                                    )}
                                                </div>
                                                <div className="text-[11.5px] text-[#94A3B8] mt-1 font-dmsans">{plan.per}</div>
                                            </div>

                                            <div className="h-px bg-[#EFF1F5]" />

                                            {/* features */}
                                            <ul className="flex flex-col gap-2 flex-1">
                                                {plan.meta?.features.map((f, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-[12.5px] text-[#334155] font-dmsans">
                                                        <span className="w-3.5 h-3.5 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ background: '#ECFDF3' }}>
                                                            <Check size={8} style={{ color: '#067647', strokeWidth: 3 }} />
                                                        </span>
                                                        {f}
                                                    </li>
                                                ))}
                                            </ul>

                                            {/* cta */}
                                            <button
                                                type="button"
                                                onClick={plan.onSelect}
                                                disabled={plan.disabled}
                                                className="w-full py-2.5 rounded-xl text-[13px] font-semibold font-dmsans transition-all disabled:opacity-60 mt-auto"
                                                style={{
                                                    background: sel ? '#3F57E0' : plan.isCurrent ? '#EEF1FE' : '#fff',
                                                    color: sel ? '#fff' : plan.isCurrent ? '#3F57E0' : '#0F172A',
                                                    border: sel || plan.isCurrent ? 'none' : '1px solid #E6E8EE',
                                                }}
                                            >
                                                {plan.isLoading ? 'Processing…' : plan.ctaLabel}
                                            </button>
                                            {plan.error && (
                                                <p className="text-[11px] text-red-500 text-center mt-1 font-dmsans">{plan.error}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ── comparison matrix ── */}
                        {!subLoading && !subError && <div className="rounded-xl border border-[#E6E8EE] bg-white" style={{ borderRadius: 12 }}>
                                {/* card header */}
                                <div className="flex items-center justify-between px-5 py-4 border-b border-[#EFF1F5]">
                                    <div>
                                        <div className="text-[14px] font-bold text-[#0F172A] font-dmsans">Compare features</div>
                                        <div className="text-[12px] text-[#94A3B8] font-dmsans mt-0.5">What unlocks with each plan</div>
                                    </div>
                                    <span className="text-[11.5px] font-semibold font-dmsans px-2.5 py-1 rounded-full" style={{ background: '#ECFDF3', color: '#067647' }}>
                                        All plans · cancel anytime
                                    </span>
                                </div>
                                {(() => {
                                    const COLS = [
                                        { key: 'free',      label: 'Free' },
                                        { key: 'weekly',    label: 'Weekly' },
                                        { key: 'monthly',   label: 'Monthly ★' },
                                        { key: 'quarterly', label: 'Quarterly' },
                                    ];
                                    return (
                                        <>
                                            {/* column headers */}
                                            <div className="grid border-b border-[#EFF1F5] bg-[#FAFBFD] px-4 py-2.5"
                                                style={{ gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr' }}>
                                                <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans">Feature</div>
                                                {COLS.map(({ key, label }) => (
                                                    <div key={key}
                                                        className="text-center text-[10.5px] font-bold uppercase tracking-[.08em] font-dmsans transition-colors"
                                                        style={{ color: selectedPlan === key ? '#3F57E0' : '#94A3B8' }}>
                                                        {label}
                                                    </div>
                                                ))}
                                            </div>
                                            {/* rows */}
                                            {COMPARE_ROWS.map((row, i) => (
                                                <div key={i} className="grid px-4 py-3 items-center"
                                                    style={{
                                                        gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr',
                                                        borderBottom: i < COMPARE_ROWS.length - 1 ? '1px solid #EFF1F5' : 'none',
                                                    }}>
                                                    <div className="text-[13px] font-semibold text-[#0F172A] font-dmsans">{row.label}</div>
                                                    {COLS.map(({ key }) => {
                                                        const active = selectedPlan === key;
                                                        return (
                                                            <div key={key}
                                                                className="text-center text-[13px] font-dmsans transition-all"
                                                                style={active
                                                                    ? { color: '#3F57E0', fontWeight: 700 }
                                                                    : { color: key === 'free' ? '#94A3B8' : '#64748B' }
                                                                }>
                                                                {row[key]}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </>
                                    );
                                })()}
                            </div>}

                        {/* ── current plan + recent invoices ── */}
                        {!subLoading && !subError && (
                            <div className="grid gap-4" style={{ gridTemplateColumns: '1.4fr 1fr' }}>

                                {/* current plan card */}
                                <div className="bg-white rounded-2xl border border-[#E6E8EE] p-5 flex flex-col gap-4">
                                    <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans">Current plan</div>

                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-[11px] flex items-center justify-center flex-shrink-0"
                                            style={{ background: isFreeUser ? '#F0FDF4' : '#EEF1FE' }}>
                                            <Sparkles size={17} style={{ color: isFreeUser ? '#16A34A' : '#3F57E0' }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[15px] font-bold tracking-tight text-[#0F172A] font-dmsans">
                                                {isFreeUser ? 'Free Trial' : (
                                                    <>
                                                        {currentCycleName
                                                            ? currentCycleName.charAt(0).toUpperCase() + currentCycleName.slice(1)
                                                            : currentTier.charAt(0).toUpperCase() + currentTier.slice(1)
                                                        } · Pro
                                                    </>
                                                )}
                                            </div>
                                            <div className="text-[11.5px] text-[#64748B] mt-0.5 font-dmsans">
                                                {isFreeUser
                                                    ? (user?.trialEndsAt
                                                        ? `Trial ends ${new Date(user.trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
                                                        : '7-day free trial')
                                                    : renewalDate
                                                        ? `Renews on ${renewalDate}`
                                                        : 'Active subscription'
                                                }
                                                {!isFreeUser && user?.subscription?.cancelAtPeriodEnd && (
                                                    <span className="text-orange-500"> · Cancels at period end</span>
                                                )}
                                            </div>
                                        </div>
                                        <span className="text-[10px] font-bold uppercase tracking-[.06em] px-2 py-1 rounded-full font-dmsans flex-shrink-0"
                                            style={isFreeUser
                                                ? { background: '#FEF9C3', color: '#854D0E' }
                                                : { background: '#ECFDF3', color: '#067647' }}>
                                            {isFreeUser ? 'Trial' : 'Active'}
                                        </span>
                                    </div>

                                    {/* usage stats */}
                                    <div className="grid grid-cols-3 rounded-xl overflow-hidden border border-[#E6E8EE]" style={{ background: '#FAFBFD' }}>
                                        {(isFreeUser ? [
                                            ['AI credits',    creditsBalance != null ? creditsBalance.toLocaleString() : '—', 'remaining'],
                                            ['Days left', (() => { if (!user?.trialEndsAt) return '—'; const d = Math.max(0, Math.ceil((new Date(user.trialEndsAt) - Date.now()) / 86400000)); return d === 0 ? 'Ended' : String(d); })(), 'of 7-day trial'],
                                            ['Plan',          'Free', 'upgrade anytime'],
                                        ] : [
                                            ['AI credits',        creditsBalance != null ? creditsBalance.toLocaleString() : '—', 'remaining'],
                                            ['Recruiter lookups', currentCycleName === 'monthly' ? '200' : currentCycleName === 'quarterly' ? '600' : '50', 'per cycle'],
                                            ['Billing cycle',     currentCycleName ? currentCycleName.charAt(0).toUpperCase() + currentCycleName.slice(1) : 'Active', 'active plan'],
                                        ]).map(([label, value, sub], i) => (
                                            <div key={i} className="p-3.5" style={{ borderRight: i < 2 ? '1px solid #E6E8EE' : 'none' }}>
                                                <div className="text-[10px] font-bold uppercase tracking-[.06em] text-[#94A3B8] font-dmsans">{label}</div>
                                                <div className="text-[19px] font-extrabold tracking-tight text-[#0F172A] font-dmsans mt-1">{value}</div>
                                                <div className="text-[10.5px] text-[#94A3B8] font-dmsans mt-0.5">{sub}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* action buttons */}
                                    <div id="cancel-section" className="flex items-center gap-2 flex-wrap">
                                        {isFreeUser ? (
                                            <p className="text-[12px] text-[#64748B] font-dmsans">
                                                Pick a plan above to unlock full access.
                                            </p>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-1.5 text-[12px] font-semibold font-dmsans text-[#334155] px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white hover:bg-[#F4F5F8] transition-colors"
                                                >
                                                    <CreditCard size={13} />
                                                    Update payment
                                                </button>
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-1.5 text-[12px] font-semibold font-dmsans text-[#334155] px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white hover:bg-[#F4F5F8] transition-colors"
                                                >
                                                    <Mail size={13} />
                                                    Change billing email
                                                </button>
                                                <div className="flex-1" />
                                                {cancelDone ? (
                                                    <span className="text-[12px] text-orange-500 font-semibold font-dmsans">Cancellation scheduled</span>
                                                ) : user?.subscription?.cancelAtPeriodEnd ? (
                                                    <span className="text-[12px] text-orange-500 font-semibold font-dmsans">Cancels at period end</span>
                                                ) : cancelConfirm ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <AlertTriangle size={12} className="text-red-500" />
                                                        <span className="text-[11.5px] text-[#334155] font-dmsans">Sure?</span>
                                                        <button
                                                            type="button"
                                                            onClick={handleCancel}
                                                            disabled={cancelLoading}
                                                            className="text-[12px] font-semibold font-dmsans text-red-500 hover:text-red-600 transition-colors disabled:opacity-60"
                                                        >
                                                            {cancelLoading ? 'Cancelling…' : 'Yes, cancel'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setCancelConfirm(false)}
                                                            className="text-[12px] font-semibold font-dmsans text-[#94A3B8] hover:text-[#334155] transition-colors"
                                                        >
                                                            Never mind
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={handleCancel}
                                                        className="text-[12px] font-semibold font-dmsans text-red-500 hover:text-red-600 transition-colors"
                                                    >
                                                        Cancel subscription
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* recent invoices card */}
                                <div className="bg-white rounded-2xl border border-[#E6E8EE] p-5 flex flex-col">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans">Recent invoices</div>
                                        <span className="text-[11.5px] font-semibold font-dmsans" style={{ color: '#3F57E0', cursor: 'default' }}>
                                            {invoices.length > 0 ? `${invoices.length} total` : ''}
                                        </span>
                                    </div>

                                    {invoicesLoading && (
                                        <div className="flex flex-col gap-3">
                                            {[1,2,3,4].map(k => (
                                                <div key={k} className="h-10 bg-gray-100 animate-pulse rounded-lg" />
                                            ))}
                                        </div>
                                    )}

                                    {!invoicesLoading && invoices.length === 0 && (
                                        <div className="flex-1 flex flex-col items-center justify-center gap-2 py-6">
                                            <FileText size={24} className="text-[#C7CBD6]" />
                                            <p className="text-[12px] text-[#94A3B8] font-dmsans">No invoices yet</p>
                                        </div>
                                    )}

                                    {!invoicesLoading && invoices.length > 0 && (
                                        <div className="flex flex-col">
                                            {invoices.slice(0, 5).map((inv, i) => (
                                                <div
                                                    key={inv.id}
                                                    className="flex items-center gap-2 py-2.5"
                                                    style={{ borderBottom: i < Math.min(invoices.length, 5) - 1 ? '1px solid #EFF1F5' : 'none' }}
                                                >
                                                    <span className="text-[13px] text-[#64748B] font-dmsans flex-1">{fmtInvoiceDate(inv.date)}</span>
                                                    <span className="text-[13px] font-bold text-[#0F172A] font-dmsans w-16 text-right">{fmtAmount(inv.amount, inv.currency)}</span>
                                                    <span className="text-[10px] font-bold uppercase tracking-[.05em] px-1.5 py-0.5 rounded-md font-dmsans mx-1"
                                                        style={{ background: inv.status === 'paid' ? '#ECFDF3' : '#FEF2F2', color: inv.status === 'paid' ? '#067647' : '#B42318' }}>
                                                        {inv.status}
                                                    </span>
                                                    {inv.invoicePdf ? (
                                                        <a
                                                            href={inv.invoicePdf}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-[12px] font-semibold font-dmsans hover:underline"
                                                            style={{ color: '#3F57E0' }}
                                                        >
                                                            PDF
                                                        </a>
                                                    ) : (
                                                        <span className="text-[12px] text-[#C7CBD6] font-dmsans w-7">PDF</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── FAQ row ── */}
                        {!subLoading && !subError && (
                            <div className="grid grid-cols-3 gap-3 pb-6">
                                {FAQ.map((f, i) => (
                                    <div key={i} className="rounded-xl border border-[#E6E8EE] bg-white p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <HelpCircle size={13} style={{ color: '#3F57E0', flexShrink: 0 }} />
                                            <div className="text-[13px] font-bold text-[#0F172A] font-dmsans">{f.q}</div>
                                        </div>
                                        <p className="text-[12px] text-[#64748B] font-dmsans leading-relaxed">{f.a}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        </>}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPage;
